import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useLocation, Link, useNavigate, useBlocker } from 'react-router-dom';

import Header from '../component/Header';
import NachObenButton from '../component/NachObenButton';
import Footer from '../component/Footer';
import PopupBox from '../component/PopupBox';
import { getParam } from '../utils/utils';

import './VeFormular.css';

import checkIcon from '../svg/check.svg';
import tooltip from '../svg/Tooltip-Info.svg';

import { VeFormularProps, DatumsfeldProps, PopupBoxPars, Option, VeListeRow, VeData, DropdownMenuProps, TooltipData, BeRow, TextbausteineRow } from '../interfaces';
import { DatumsFormatOptions, FnType, BoxType } from '../constants';
import BackendProvider from '../context/BackendContext';

type PopupResult = 'ja' | 'nein' | 'abbrechen';

const alertMessages: Record<string, string> = {
  FIRST_DATE_FIELD_NOT_EMPTY: 'Das erste Datierungsfeld darf nicht leer sein, wenn im zweiten noch ein Datum Steht. Wenn Sie die Datierung löschen wollen, löschen Sie bitte zuerst das zweite Datierungsfeld.',
  LAST_DATE_BEFORE_FIRST_DATE: "Das Datum 'Datierung bis' darf nicht vor dem Datum 'Datierung von' liegen",
  CHECK_DATE_ENTRYS: 'Bei einer Änderung des Datumsformats werden die Datierungseinträge angepasst. Bitte überprüfen Sie Ihre Einträge.',
  SHOULD_DAT_FINDBUCH_BE_ADJUSTED: "Die Datierung wurde geändert, soll der Eintrag für 'Datierung Findbuch' angepasst werden?",
  EMPTY_OBLIGATORY_FIELDS: 'Eins oder mehrere der Pflichtfelder sind noch leer.',
  SHOULD_GESPERRT_BIS_VE_RECALCULATED: "Möchten Sie den Eintrag im Feld 'gesperrt bis' neu berechnen lassen?",
  GESPERRT_BIS_DOES_NOT_MATCH_WITH_SPERRFRIST: "Das Feld 'gesperrt bis' stimmt nicht mit der Sperrfrist überein.",
  DATE_FIELDS_NOT_VALID: 'Ein Datumsfeld ist nicht korrekt ausgefüllt.',
  INVALID_DATE: 'Ein Datumsfeld ist ungültig.'
};

const mainFields: Array<string> = ['ob_f20', 'ob_f2', 'ob_f41', 'ob_f42', 'ob_f30', 'ob_f31', 'ob_f3', 'ob_f37', 'ob_f12', 'ob_f32'];

function VeFormular() {
  const location = useLocation();
  const [veUrlParams] = useSearchParams();
  const beId = getParam(veUrlParams, 'beId');
  const beAbk = getParam(veUrlParams, 'abk');
  const beName = getParam(veUrlParams, 'name');
  const fId = getParam(veUrlParams, 'fId');
  const navigate = useNavigate();
  const beRow:BeRow = { id: Number(beId), abk: beAbk, name: beName, f_id: Number(fId) };
  const [veId, setVeId] = useState<number>(Number(getParam(veUrlParams, 'veId')));
  const [veList, setVeList] = useState<Array<VeListeRow>>([]);
  const [veListLoaded, setVeListLoaded] = useState(false);
  const [veDataRows, setVeDataRows] = useState<Array<VeData>>([]);
  const [dateFormat, setDateFormat] = useState<number>(DatumsFormatOptions.MONTH_YEAR);
  const urlParams = new URLSearchParams({ beId: beRow.id.toString(), abk: beRow.abk, name: beRow.name, fId: beRow.f_id.toString() }).toString();
  const urlPrintParams = new URLSearchParams({ veId: veId.toString(), abk: beRow.abk, name: beRow.name, fId: beRow.f_id.toString() }).toString();

  const [popupBoxOn, setPopupBoxOn] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<Array<TooltipData>>([]);
  const [resolveConfirmation, setResolveConfirmation] = useState<((value: PopupResult) => void) | null>(null);
  
  const autosperrfrist = useRef();
  const veListRef = useRef(veList);
  const datierungVon = useRef('');
  const datierungBis = useRef('');
  const textblocks = useRef<Array<TextbausteineRow>>([]);
  const focusedInput = useRef<HTMLTextAreaElement>(null);
  const initialFormValuesRef = useRef<Record<string, string> | null>(null);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const continueAfterSaveRef = useRef(false);
  const navigationInProgressRef = useRef(false);
  const skipBlockerRef = useRef(false);
  
  const isDirty = useMemo(() => {
    if (!initialFormValuesRef.current) return false;
    return JSON.stringify(extractValues(veDataRows)) !== JSON.stringify(initialFormValuesRef.current);
  }, [veDataRows]);

  const blocker = useBlocker(isDirty);

  const getPositionInListAsCallback = useCallback((_veId:number, _veList:Array<VeListeRow>) => {
    let position = 0;
    for (let i = 0; i < _veList.length; i++) {
      if (Number(_veList[i].id) === Number(_veId)) {
        position = i;
      }
    }
    
    return position;
  }, []);

  const [positionInList, setPositionInList] = useState<number>(0);

  async function getVeList() {
    const res = await fetch('/webclient/php/ve_liste.php?be_id=' + beId + '&sortedBy=ob_f41&sortingOrder=true&filterText=');

    if (!res.ok) {
      throw new Error('Anfrage fehlgeschlagen');
    }
    
    const data = await res.json();

    setVeList(data);

    setVeListLoaded(true);

    return data;
  }

  useEffect(() => {
    async function init() {
      try {
        const res = await fetch('/webclient/php/textbausteine.php');

        if (!res.ok) {
          throw new Error('Anfrage fehlgeschlagen');
        }

        const data = await res.json();

        textblocks.current = data;

        await getVeList();
      } catch (err) {
        console.log(err);
      }
    }

    init();
  }, []);

  useEffect(() => { 
    setPositionInList(getPositionInListAsCallback(veId, veList));
  }, [veId, getPositionInListAsCallback, veList]);

  useEffect(() => {
    if (!veListLoaded) {
      return;
    }
    loadData();
  }, [veId, veListLoaded]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const requestNavigation = useCallback(
    async (action: () => void) => {
      if (!isDirty) {
        action();
        return;
      }

      pendingNavigationRef.current = action;

      showPopupBox(
        4,
        'Ungespeicherte Änderungen',
        'Es liegen nicht gespeicherte Änderungen vor. Möchten Sie die Änderungen speichern?'
      );

      const choice = await openDialog();

      if (choice === 'abbrechen') {
        blocker.reset?.();
        pendingNavigationRef.current = null;
        navigationInProgressRef.current = false;
        return;
      }

      if (choice === 'nein') {
        skipBlockerRef.current = true;
        pendingNavigationRef.current?.();
        pendingNavigationRef.current = null;
        return;
      }

      continueAfterSaveRef.current = true;
      submitButton.current = '';
      submitForm();
    },
    [isDirty, submitForm]
  );

  useEffect(() => {
    if (skipBlockerRef.current) {
      skipBlockerRef.current = false;
      return;
    }

    if (
      blocker.state === 'blocked' &&
      !navigationInProgressRef.current
    ) {
      navigationInProgressRef.current = true;

      requestNavigation(() => {
        blocker.proceed();
        navigationInProgressRef.current = false;
      });
    }
  }, [blocker.state, requestNavigation]);

  useEffect(() => {
    if (blocker.state === 'unblocked') {
      navigationInProgressRef.current = false;
    }
  }, [blocker.state]);

  function getFieldValue(fieldName:string) {
    const field = veDataRows.find((f) => f.fn_lowername === fieldName);
    if (field !== undefined) {
      return field.ob_content;
    }

    return '';
  }

  function loadData() {
    let url = '/webclient/php/ve_detail.php?f_id=' + stableBeRow.current.f_id + '&ob_id=' + veId;
    url += '&b_id=' + stableBeRow.current.id;

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          console.log('errr');
          throw new Error('Anfrage fehlgeschlagen');
        }
        return res.json();
      })
      .then((data) => {
        setVeDataRows(data.ve_data); 
        setDateFormat(data.date_format as number);
        setPositionInList(getPositionInListAsCallback(veId, veList));
        autosperrfrist.current = data.autosperrfrist;  
        initShowHelp(data.ve_data); 
        initialFormValuesRef.current = structuredClone(extractValues(data.ve_data));
        focusedInput.current?.focus();
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function handleOnclickNavigation(button:string) {
    switch (button) {
      case 'first':
        setVeId(veList[0].id);
        break;
      case 'previous':
        if (positionInList === 0) {
          setVeId(veList[0].id);
        } else {
          setVeId(veList[positionInList - 1].id);
        }
        break;
      case 'next':
        if (positionInList === veList.length - 1) {
          setVeId(veList[veList.length - 1].id);
        } else {
          setVeId(veList[positionInList + 1].id);
        }
        break;
      case 'last':
        setVeId(veList[veList.length - 1].id);
        break;
      default:
        console.log("unknown value for 'button' in VeFormular.tsx => handleOnclickNavigation");
    }
  }

  function initShowHelp(data: Array<VeData>) {
    setShowHelp(data.map((dataRow) => ({ fieldName: dataRow.fn_lowername, value: false })));
  }

  function getShowHelpValue(fieldName: string):boolean {
    const item = showHelp.find((f) => f.fieldName === fieldName); 
    if (item !== undefined) {
      return item.value;
    }
    return false;
  }
  
  function handleToggleHelp(fieldName: string) {
    setShowHelp(showHelp.map((row: TooltipData) => {
      if (row.fieldName === fieldName) {
        return { ...row, value: !row.value };
      }

      return row;
    }));
  }

  function handleMouseOverHelp(fieldName: string) {
    setShowHelp(showHelp.map((row: TooltipData) => {
      if (row.fieldName === fieldName) {
        return { ...row, value: true };
      }

      return row;
    }));
  }

  function handleMouseLeaveHelp(fieldName: string) {
    setShowHelp(showHelp.map((row: TooltipData) => {
      if (row.fieldName === fieldName) {
        return { ...row, value: false };
      }

      return row;
    }));
  }

  const isCoarsePointer = () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

  const popupBoxParsRef = useRef<PopupBoxPars>();

  const showPopupBox = useCallback((kind:number, title:string, text:string) => {
    popupBoxParsRef.current = {
      kind: kind,
      title: title,
      text: text
    };
    setPopupBoxOn(true);
  }, []);

  const hidePopupBox = useCallback(() => {
    setPopupBoxOn(false);
  }, []);

  const openDialog = (): Promise<PopupResult> => new Promise((resolve) => {
    setResolveConfirmation(() => resolve);
  });

  const handleConfirm = (): void => {
    setPopupBoxOn(false);
    resolveConfirmation?.('ja'); 
  };

  const handleReject = (): void => {
    setPopupBoxOn(false);
    resolveConfirmation?.('nein'); 
  };

  const handleCancel = (): void => {
    setPopupBoxOn(false);
    resolveConfirmation?.('abbrechen'); 
  };

  const stableBeRow = useRef(beRow);

  const getDatierung = useCallback((field: string, value: string) => {
    if (field === 'ob_f30') {
      datierungVon.current = value;
    } else {
      datierungBis.current = value;
    }
  }, []);

  const isDatierungVonComplete = useRef(true);
  const isDatierungBisComplete = useRef(true);

  const isDateCompleteCallback = useCallback((field: string, isComplete: boolean) => {
    if (field === 'ob_f30') {
      isDatierungVonComplete.current = isComplete;
    } else {
      isDatierungBisComplete.current = isComplete;
    }
  }, []);

  const isDatierungVonValid = useRef(true);
  const isDatierungBisValid = useRef(true);

  const isDateValidCallback = useCallback((field: string, isValid: boolean) => {
    if (field === 'ob_f30') {
      isDatierungVonValid.current = isValid;
    } else {
      isDatierungBisValid.current = isValid;
    }
  }, []);

  const validDatierung = useRef<boolean>();

  async function checkDatierung() {
    if (!isDatierungVonComplete.current || !isDatierungBisComplete.current) {
      showPopupBox(BoxType.ALERT, 'Speichern nicht möglich', alertMessages.DATE_FIELDS_NOT_VALID);
      const result = await openDialog();
      if (result === 'ja') {
        return false;
      } 
    }
    
    if (datierungVon.current === '' && datierungBis.current !== '') {
      showPopupBox(BoxType.ALERT, 'Datumsfehler', alertMessages.FIRST_DATE_FIELD_NOT_EMPTY);
      const result = await openDialog();
      if (result === 'ja') {
        return false;
      }
    }
    
    if (datierungBis.current !== '' && datierungVon.current > datierungBis.current) {
      showPopupBox(BoxType.ALERT, 'Datumsfehler', alertMessages.LAST_DATE_BEFORE_FIRST_DATE);
      const result = await openDialog();
      if (result === 'ja') {
        return false;
      }
    }

    if (!isDatierungVonValid.current || !isDatierungBisValid.current) {
      showPopupBox(BoxType.ALERT, 'Speichern nicht möglich', alertMessages.INVALID_DATE);
      const result = await openDialog();
      if (result === 'ja') {
        return false;
      } 
    }

    return true;
  }

  const completecPflichtfelder = useRef<boolean>();

  async function checkPflichtfelder() {
    let isFilled = true;

    for (let i = 0; i < veDataRows.length; i++) {
      if (veDataRows[i].fn_duty === '1') {
        if (veDataRows[i].fn_lowername === 'ob_f30') {
          if (datierungVon.current === '') {
            isFilled = false;
          }
        } else if (veDataRows[i].fn_lowername === 'ob_f31') {
          if (datierungBis.current === '') {
            isFilled = false;
          }
        } else if (veDataRows[i].ob_content === '') {
          isFilled = false;
        } 
      }
    }

    if (!isFilled) {
      showPopupBox(BoxType.ALERT, 'Speichern nicht möglich', alertMessages.EMPTY_OBLIGATORY_FIELDS);
      const result = await openDialog();
      if (result === 'ja') {
        return false;
      }
    }
    
    return true;
  }

  function setFieldValue(fieldName: string, fieldValue: string) {
    setVeDataRows(veDataRows.map((row: VeData) => {
      if (row.fn_lowername === fieldName) {
        return { ...row, ob_content: fieldValue };
      }

      return row;
    }));
  }

  async function calculateSperrfrist(event: React.FocusEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    if (isDatierungBisComplete.current && value !== '' && datierungBis.current !== '') {
      showPopupBox(BoxType.CONFIRM_JA_NEIN, 'Sperrfristrelevante Änderung', alertMessages.SHOULD_GESPERRT_BIS_VE_RECALCULATED);
      const result = await openDialog();
      if (result === 'ja') {
        const newYear = parseInt(value) + parseInt(datierungBis.current.slice(0, 4));
        
        let newDate = '';

        if (dateFormat === DatumsFormatOptions.YEAR) { 
          newDate = newYear.toString() + '-12-31';
        }
        if (dateFormat === DatumsFormatOptions.MONTH_YEAR) {
          newDate = newYear.toString() + '-' + datierungBis.current.slice(4, 6) + '-01';
        }
        if (dateFormat === DatumsFormatOptions.DAY_MONTH_YEAR) {
          newDate = newYear.toString() + '-' + datierungBis.current.slice(4, 6) + '-' + datierungBis.current.slice(6, 8);
        }

        setFieldValue('ob_f32', newDate);
      }
    }
  }

  const dateChanged = useRef(false);

  const setDateChanged = useCallback((isChanged: boolean) => {
    dateChanged.current = isChanged;
  }, []);

  const submitButton = useRef('');

  function handleSubmit(event:React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submitForm();
  }

  async function submitForm() {
    let changeDatFindbuch = false;

    if (!(await checkDatierung())) return;
    if (!(await checkPflichtfelder())) return;
    
    if (dateChanged.current && veId !== -1) {
      showPopupBox(BoxType.CONFIRM_JA_NEIN, 'Datierungsänderung', alertMessages.SHOULD_DAT_FINDBUCH_BE_ADJUSTED);
      const result = await openDialog();
      if (result === 'ja') {
        changeDatFindbuch = true;
      }
    }

    const url = '/webclient/php/ve_detail.php'
      + '?action=save'
      + '&ob_format=' + dateFormat
      + '&change_dat_findbuch=' + changeDatFindbuch
      + '&b_id=' + stableBeRow.current.id
      + '&ob_id=' + veId
      + '&f_id=' + stableBeRow.current.f_id;

    const updateData = document.getElementById('updateData') as HTMLFormElement | null;

    const form = updateData ? new FormData(updateData) : undefined;

    try {
      const res = await fetch(url, { method: 'POST', body: form });

      if (!res.ok) {
        throw new Error('Anfrage fehlgeschlagen');
      }

      const data = await res.json();

      if (submitButton.current === 'saveAndBack') {
        navigate(`/veliste?${urlParams}`);
        return;
      } 
      
      if (submitButton.current === 'saveAndNew') {
        /*
        if (veId === -1) {           
          loadData();
        }
        */
        setVeId(-1);
        dateChanged.current = false;
        return;
      } 
      
      setVeDataRows(data.ve_data); 
      setDateFormat(data.date_format as number);
      autosperrfrist.current = data.autosperrfrist;  
      initShowHelp(data.ve_data);
      initialFormValuesRef.current = structuredClone(extractValues(data.ve_data));
      setVeId(data.ob_id);
      /*
      if (veId !== -1) {
        dateChanged.current = false;
      }
      */
      if (veId === -1) {
        await getVeList();
      } 

      dateChanged.current = false;

      if (continueAfterSaveRef.current && pendingNavigationRef.current) {
        pendingNavigationRef.current();
        pendingNavigationRef.current = null;
        continueAfterSaveRef.current = false;
      }
    } catch (err) {
      console.log(err);
    }
  }

  function undo() {
    setFieldValue(lastInput.current.name, lastInput.current.value);
  }

  const changeDateFormat = useCallback((option:number) => {
    setDateFormat(option);
  }, []);

  const changeDateFormatCallback = useCallback((option:number) => {
    changeDateFormat(option);
  }, []);

  function keyIsNumber(key:string) {
    const regex = /^[0-9]$/;

    return regex.test(key);
  }

  function getMaxLength(inputformat:string|undefined) {
    const length = inputformat?.replace('Zeichen', '');
    return Number(length?.trim());
  }

  function getDateValue(d:string) {
    return d.substring(0, 10);
  }

  function handleKeyDown(event:React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.altKey && keyIsNumber(event.key)) {
      const activInput = event.currentTarget;
      const start = activInput.selectionStart!;
      const end = activInput.selectionEnd!;
      const before = activInput.value.substring(0, start);
      const after = activInput.value.substring(end);
      let position;

      if (event.key === '0') {
        activInput.value = before + textblocks.current[9].txbText + after;
        position = start + textblocks.current[9].txbText.length;
      } else {
        activInput.value = before + textblocks.current[Number(event.key) - 1].txbText + after;
        position = start + textblocks.current[Number(event.key) - 1].txbText.length;
      }

      activInput.setSelectionRange(position, position);
    }
  }

  const lastInput = useRef({ name: '', value: '' });

  function handleChangeInput({ target }:React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>) {
    const { name, value } = target;

    if (lastInput.current.name !== name) {
      lastInput.current.name = name;
      lastInput.current.value = getFieldValue(name);
    }

    setFieldValue(name, value);
  }

  function getLabel(fnLowername:string):string|undefined {
    const rw = veDataRows.find((row) => row.fn_lowername === fnLowername);

    return rw?.fn_bez;
  }

  function getContent(fnLowername:string):string|undefined {
    const rw = veDataRows.find((row) => row.fn_lowername === fnLowername);

    return rw?.ob_content;
  }

  function getVeDataRow(fnLowername:string):VeData {
    const dataRow = veDataRows.find((row) => row.fn_lowername === fnLowername);
    if (typeof dataRow === 'undefined') {
      return { fn_lowername: '',
        fn_bez: '',
        fn_inputformat: '',
        fn_duty: '',
        fn_type: 0,
        fn_status_zeilen: '',
        ob_content: '',
        options: [] };
    }
    return dataRow;
  }

  function getSubmitButton(button:string) {
    submitButton.current = button;
  }

  function handleClickPrintButton(event: React.MouseEvent) {
    event.stopPropagation();
    window.open(`/print?${urlPrintParams}`, '_blank');
  }

  function filterTextLength(text:string|undefined):string|undefined {
    if (window.innerWidth < 1200 && typeof text === 'string' && text.length > 25) {
      return text.slice(0, 25) + '...';
    }
    if (window.innerWidth > 1199 && typeof text === 'string' && text.length > 100) {
      return text.slice(0, 100) + '...';
    }
    return text;
  }
  
  function extractValues(rows: VeData[]) {
    const values: Record<string, string> = {};

    rows.forEach((r) => {
      values[r.fn_lowername] = r.ob_content ?? '';
    });

    return values;
  }
  
  return (
    <>
      {/* <BackendProvider><Header /></BackendProvider> */}
      <Header />
      <main role="main" className="ve-formular">
        <div className="ve-formular-header">
          <nav role="navigation" aria-label="Brotkrumen" className="breadcrumb">
            <ul>
              <li>
                <Link 
                  to="/bestandsregie"
                  className="button-as-link" 
                  data-text="Bestandsregie"
                >
                  Bestandsregie
                </Link>
              </li>
              <li>
                <Link 
                  to={`/veliste?${urlParams}`} 
                  className="button-as-link" 
                  data-text={filterTextLength('Bestand: ' + beRow.abk + ' - ' + beRow.name)}
                >
                  {filterTextLength('Bestand: ' + beRow.abk + ' - ' + beRow.name)}
                </Link>
              </li>
              <li aria-current="page">{ veId !== -1 ? filterTextLength(getContent('ob_f20')) : 'Neuen Datensatz anlegen' }</li>
            </ul>
          </nav>
          
          { veId !== -1 ? <h1>{filterTextLength(getContent('ob_f20'))}</h1> : null }
            
          <div role="toolbar" aria-label="Menüleiste" className="toolbar">
            <div className="ve-tools">
              <div className="ve-ansicht">
                <h2>Ansicht</h2>
                <div className="ve-ansicht-buttons">
                  <DropdownMenu 
                    option={dateFormat} 
                    changeDateFormat={changeDateFormat} 
                    setDateChanged={setDateChanged} 
                    showPopupBox={showPopupBox} 
                    openDialog={openDialog} 
                  />
                  <button 
                    type="button" 
                    className="cancel-button" 
                    onClick={undo} 
                    title="Änderung im letzten bearbeiteten Feld rückgängig machen"
                  >
                    <span>Schritt zurück</span>
                    { /* <img src={cancel} width="" height="" alt="" /> */ }
                  </button>
                </div>
              </div>
              <div className="ve-optionen">
                <h2>Optionen</h2>
                <button 
                  type="submit" 
                  form="updateData" 
                  className="save-button" 
                  onClick={() => { getSubmitButton(''); }}
                >
                  <span>
                    Speichern
                  </span>
                </button>
                <button 
                  type="submit" 
                  form="updateData" 
                  className="save-n-new-button" 
                  onClick={() => { getSubmitButton('saveAndNew'); }}
                >
                  <span>
                    Speichern und neue VE anlegen
                  </span>
                </button>
                <button 
                  type="submit" 
                  form="updateData" 
                  className="save-n-back" 
                  onClick={() => { getSubmitButton('saveAndBack'); }}
                >
                  <span>
                    Speichern und zurück
                  </span>
                </button>
                { veId !== -1 ? (
                  <button 
                    type="button" 
                    className="print-button" 
                    onClick={handleClickPrintButton}
                    title="aktuelle Ansicht drucken"
                  >
                    <span>
                      Drucken
                    </span>
                    { /* <img <img src={print} width="" height="" alt="" /> */ }
                  </button> 
                )
                  : 
                  null }
              </div>
            </div>

            { veId !== -1 ? (
              <div className="ve-form-navigation">
                <h2>Zu einer anderen VE springen</h2>
                <div>
                  <div className="align-items">
                    <button 
                      className={`button-as-link first-icon ${positionInList === 0 ? 'invisible' : ''}`} 
                      data-text="erste" 
                      onClick={() => {
                        requestNavigation(() => handleOnclickNavigation('first'));
                      }}
                    >
                      erste
                    </button>
                  </div>
                  <div className="align-items">
                    <button 
                      className={`button-as-link previous-icon ${positionInList === 0 ? 'invisible' : ''}`} 
                      data-text="vorherige" 
                      onClick={() => {
                        requestNavigation(() => handleOnclickNavigation('previous'));
                      }}
                    >
                      vorherige
                    </button>
                  </div>

                  <div className="align-items">
                    <button 
                      className={`button-as-link next-icon ${positionInList === veList.length - 1 ? 'invisible' : ''}`} 
                      data-text="nächste" 
                      onClick={() => {
                        requestNavigation(() => handleOnclickNavigation('next'));
                      }}
                    >
                      nächste
                    </button>
                  </div>
                  <div className="align-items">
                    <button 
                      className={`button-as-link last-icon ${positionInList === veList.length - 1 ? 'invisible' : ''}`} 
                      data-text="letzte" 
                      onClick={() => {
                        requestNavigation(() => handleOnclickNavigation('last'));
                      }}
                    >
                      letzte
                    </button>
                  </div>
                </div>
              </div>
            )
              :
              null }
          </div>   
        </div>
        <form aria-label="Verzeichnungsformular" className="ve-form" id="updateData" name="" onSubmit={handleSubmit}>
          <div>
            <div className="ve-prop">
              <div className="ve-label">
                <label htmlFor="ob_f20">
                  {getVeDataRow('ob_f20')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f20')?.fn_bez}*</i> : getVeDataRow('ob_f20')?.fn_bez}
                </label>
              </div>
              <div className="ve-input">
                <textarea 
                  aria-required={getVeDataRow('ob_f20')?.fn_duty === '1'}
                  ref={focusedInput}
                  required={getVeDataRow('ob_f20')?.fn_duty === '1'}
                  rows={5} 
                  maxLength={getMaxLength(getVeDataRow('ob_f20')?.fn_inputformat)} 
                  name={getVeDataRow('ob_f20')?.fn_lowername} 
                  value={getVeDataRow('ob_f20')?.ob_content} 
                  onChange={handleChangeInput} 
                  onKeyDown={handleKeyDown} 
                /> 
              </div>
              <div className="ve-tooltip-icon-div">
                {getVeDataRow('ob_f20').fn_status_zeilen !== '' && (
                  <button 
                    type="button" 
                    aria-describedby="ob_f20-tooltip"
                    tabIndex={0}
                    className="ve-tooltip-icon" 
                    onMouseEnter={() => {
                      if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f20')!.fn_lowername);
                    }}
                    onMouseLeave={() => {
                      if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f20')!.fn_lowername);
                    }}
                    onPointerDown={(e) => {
                      if (isCoarsePointer()) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleHelp(getVeDataRow('ob_f20')!.fn_lowername);
                      }
                    }}  
                    onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f20')?.fn_lowername)} 
                    onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f20')?.fn_lowername)} 
                  > 
                    <img aria-hidden="true" src={tooltip} alt="" />
                  </button>
                )}
              </div>
            </div>
            {getVeDataRow('ob_f20')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f20')?.fn_lowername) && (
              <div role="tooltip" id="ob_f20-tooltip" className="ve-help-text">
                {getVeDataRow('ob_f20')?.fn_status_zeilen}
              </div>
            )}
          </div>
          <div className="ve-form-short-fields">
            { getVeDataRow('ob_f2').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f2">
                      {getVeDataRow('ob_f2')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f2')?.fn_bez}*</i> : getVeDataRow('ob_f2')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <input 
                      type="text" 
                      aria-required={getVeDataRow('ob_f2')?.fn_duty === '1'}
                      required={getVeDataRow('ob_f2')?.fn_duty === '1'}
                      maxLength={255} 
                      name={getVeDataRow('ob_f2')?.fn_lowername} 
                      value={getVeDataRow('ob_f2')?.ob_content} 
                      onChange={handleChangeInput} 
                      onKeyDown={handleKeyDown} 
                    /> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f2').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f2-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f2')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f2')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f2')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f2')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f2')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f2')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f2')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f2-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f2')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f41').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f41">
                      {getVeDataRow('ob_f41')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f41')?.fn_bez}*</i> : getVeDataRow('ob_f41')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <input 
                      type="text" 
                      aria-required={getVeDataRow('ob_f41')?.fn_duty === '1'}
                      required={getVeDataRow('ob_f41')?.fn_duty === '1'}
                      name={getVeDataRow('ob_f41')?.fn_lowername} 
                      value={getVeDataRow('ob_f41')?.ob_content === '' ? '' : Number(getVeDataRow('ob_f41')?.ob_content)} 
                      onChange={handleChangeInput} 
                      onKeyDown={handleKeyDown} 
                    /> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f41').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f41-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f41')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f41')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f41')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f41')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f41')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f41')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f41')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f41-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f41')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f42').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f42">{getVeDataRow('ob_f42')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f42')?.fn_bez}*</i> : getVeDataRow('ob_f42')?.fn_bez}</label>
                  </div>
                  <div className="ve-input">
                    <select className="custom-select" name={getVeDataRow('ob_f42')?.fn_lowername} value={getVeDataRow('ob_f42')?.ob_content} onChange={handleChangeInput}>
                      <option />
                      {getVeDataRow('ob_f42')?.options.map((option:Option) => <option key={option.id} value={option.id}>{option.text}</option>)}
                    </select> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f42').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f42-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f42')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f42')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f42')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f42')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f42')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f42')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f42')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f42-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f42')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f30').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f30">
                      {getVeDataRow('ob_f30')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f30')?.fn_bez}*</i> : getVeDataRow('ob_f30')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <div className="datumsfeld">
                      <Datumsfeld 
                        name={getVeDataRow('ob_f30')?.fn_lowername} 
                        value={getVeDataRow('ob_f30').ob_content} 
                        required={getVeDataRow('ob_f30')?.fn_duty === '1'}
                        veData={veDataRows} 
                        setVeData={setVeDataRows} 
                        format={dateFormat} 
                        setDateChanged={setDateChanged}  
                        getDatierung={getDatierung}
                        isDateCompleteCallback={isDateCompleteCallback}
                        isDateValidCallback={isDateValidCallback}
                      />
                    </div> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f30').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        tabIndex={0}
                        aria-describedby="ob_f30-tooltip"
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f30')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f30')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f30')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f30')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f30')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f30')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f30')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f30-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f30')?.fn_status_zeilen}
                  </div>
                )}
              </div> 
            )}
            { getVeDataRow('ob_f31').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f31">
                      {getVeDataRow('ob_f31')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f31')?.fn_bez}*</i> : getVeDataRow('ob_f31')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <div className="datumsfeld">
                      <Datumsfeld 
                        name={getVeDataRow('ob_f31')?.fn_lowername} 
                        value={getVeDataRow('ob_f31').ob_content}
                        required={getVeDataRow('ob_f31')?.fn_duty === '1'} 
                        veData={veDataRows} 
                        setVeData={setVeDataRows} 
                        format={dateFormat} 
                        setDateChanged={setDateChanged}  
                        getDatierung={getDatierung}
                        isDateCompleteCallback={isDateCompleteCallback}
                        isDateValidCallback={isDateValidCallback}
                      />
                    </div> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f31').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        tabIndex={0}
                        aria-describedby="ob_f31-tooltip"
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f31')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f31')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f31')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f31')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f31')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f31')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f31')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f31-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f31')?.fn_status_zeilen}
                  </div>
                )}
              </div> 
            )}
            { getVeDataRow('ob_f3').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f3">
                      {getVeDataRow('ob_f3')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f3')?.fn_bez}*</i> : getVeDataRow('ob_f3')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <input 
                      type="text" 
                      aria-required={getVeDataRow('ob_f3')?.fn_duty === '1'}
                      required={getVeDataRow('ob_f3')?.fn_duty === '1'}
                      maxLength={255} 
                      name={getVeDataRow('ob_f3')?.fn_lowername} 
                      value={getVeDataRow('ob_f3')?.ob_content} 
                      onChange={handleChangeInput} 
                      onKeyDown={handleKeyDown} 
                    /> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f3').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f3-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f3')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f3')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f3')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f3')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f3')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f3')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f3')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f3-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f3')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f37').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f37">
                      {getVeDataRow('ob_f37')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f37')?.fn_bez}*</i> : getVeDataRow('ob_f37')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    { autosperrfrist.current === 1 
                      ? 
                      ( 
                        <input 
                          type="text"
                          aria-required={getVeDataRow('ob_f37')?.fn_duty === '1'}
                          required={getVeDataRow('ob_f37')?.fn_duty === '1'}
                          name={getVeDataRow('ob_f37')?.fn_lowername} 
                          value={getVeDataRow('ob_f37')?.ob_content === '' ? '' : parseInt(getVeDataRow('ob_f37')?.ob_content)} 
                          onChange={handleChangeInput} 
                          onKeyDown={handleKeyDown} 
                          onBlur={calculateSperrfrist} 
                        /> 
                      )
                      :
                      ( 
                        <input 
                          type="text"
                          aria-required={getVeDataRow('ob_f37')?.fn_duty === '1'}
                          required={getVeDataRow('ob_f37')?.fn_duty === '1'}
                          name={getVeDataRow('ob_f37')?.fn_lowername} 
                          value={getVeDataRow('ob_f37')?.ob_content === '' ? '' : parseInt(getVeDataRow('ob_f37')?.ob_content)} 
                          onChange={handleChangeInput} 
                          onKeyDown={handleKeyDown} 
                        />
                      ) }
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f37').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f37-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f37')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f37')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f37')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f37')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f37')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f37')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f37')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f37-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f37')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f12').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f12">
                      {getVeDataRow('ob_f12')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f12')?.fn_bez}*</i> : getVeDataRow('ob_f12')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <input 
                      type="text" 
                      aria-required={getVeDataRow('ob_f12')?.fn_duty === '1'}
                      required={getVeDataRow('ob_f12')?.fn_duty === '1'}
                      maxLength={255} 
                      name={getVeDataRow('ob_f12')?.fn_lowername} 
                      value={getVeDataRow('ob_f12')?.ob_content} 
                      onChange={handleChangeInput} 
                      onKeyDown={handleKeyDown} 
                    /> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f12').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f12-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f12')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f12')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f12')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f12')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f12')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f12')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f12')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f12-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f12')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
            { getVeDataRow('ob_f32').fn_lowername !== '' && (
              <div>
                <div className="ve-prop">
                  <div className="ve-label">
                    <label htmlFor="ob_f32">
                      {getVeDataRow('ob_f32')?.fn_duty === '1' ? <i>{getVeDataRow('ob_f32')?.fn_bez}*</i> : getVeDataRow('ob_f32')?.fn_bez}
                    </label>
                  </div>
                  <div className="ve-input">
                    <input 
                      type="date" 
                      aria-required={getVeDataRow('ob_f32')?.fn_duty === '1'}
                      required={getVeDataRow('ob_f32')?.fn_duty === '1'}
                      name={getVeDataRow('ob_f32').fn_lowername} 
                      value={getDateValue(getVeDataRow('ob_f32')?.ob_content)} 
                      onChange={handleChangeInput} 
                    /> 
                  </div>
                  <div className="ve-tooltip-icon-div">
                    {getVeDataRow('ob_f32').fn_status_zeilen !== '' && (
                      <button 
                        type="button" 
                        aria-describedby="ob_f32-tooltip"
                        tabIndex={0}
                        className="ve-tooltip-icon" 
                        onMouseEnter={() => {
                          if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow('ob_f32')!.fn_lowername);
                        }}
                        onMouseLeave={() => {
                          if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow('ob_f32')!.fn_lowername);
                        }}
                        onPointerDown={(e) => {
                          if (isCoarsePointer()) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleHelp(getVeDataRow('ob_f32')!.fn_lowername);
                          }
                        }}  
                        onFocus={(e) => handleMouseOverHelp(getVeDataRow('ob_f32')?.fn_lowername)} 
                        onBlur={(e) => handleMouseLeaveHelp(getVeDataRow('ob_f32')?.fn_lowername)} 
                      > 
                        <img aria-hidden="true" src={tooltip} alt="" />
                      </button>
                    )}
                  </div>
                </div>
                {getVeDataRow('ob_f32')?.fn_status_zeilen !== '' && getShowHelpValue(getVeDataRow('ob_f32')?.fn_lowername) && (
                  <div role="tooltip" id="ob_f32-tooltip" className="ve-help-text-sm">
                    {getVeDataRow('ob_f32')?.fn_status_zeilen}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {veDataRows.map((row: VeData) => {
            let input;

            if (!mainFields.includes(row.fn_lowername)) {
              if (row.fn_type === FnType.TEXT && row.fn_lowername !== 'ob_f42') {
                if (row.fn_inputformat === '4000' || row.fn_inputformat === '4000 Zeichen' || row.fn_inputformat === '64000' || row.fn_inputformat === '64000 Zeichen') {
                  input = <textarea rows={5} maxLength={getMaxLength(row.fn_inputformat)} name={row.fn_lowername} value={row.ob_content} onChange={handleChangeInput} onKeyDown={handleKeyDown} />;
                } else if (row.fn_inputformat === '255' || row.fn_inputformat === '255 Zeichen') {
                  input = <input type="text" maxLength={255} name={row.fn_lowername} value={row.ob_content} onChange={handleChangeInput} onKeyDown={handleKeyDown} />;
                } else if (row.fn_inputformat === 'Dezimalzahl') {
                  if (row.fn_lowername === 'ob_f37' && autosperrfrist.current === 1) {
                    input = <input type="text" name={row.fn_lowername} value={row.ob_content === '' ? '' : parseInt(row.ob_content)} onChange={handleChangeInput} onKeyDown={handleKeyDown} onBlur={calculateSperrfrist} />;
                  } else if (row.fn_lowername === 'ob_f41') {
                    input = <input type="text" name={row.fn_lowername} value={row.ob_content === '' ? '' : parseInt(row.ob_content)} onChange={handleChangeInput} onKeyDown={handleKeyDown} />;
                  } else {
                    input = <input type="text" name={row.fn_lowername} value={row.ob_content} onChange={handleChangeInput} onKeyDown={handleKeyDown} />;
                  }
                } else if (row.fn_inputformat === 'tt.mm.jjjj') {
                  input = (
                    <div className="datumsfeld">
                      <Datumsfeld 
                        name={row.fn_lowername} 
                        value={row.ob_content} 
                        required={row.fn_duty === '1'}
                        veData={veDataRows} 
                        setVeData={setVeDataRows} 
                        format={dateFormat} 
                        setDateChanged={setDateChanged}  
                        getDatierung={getDatierung}
                        isDateCompleteCallback={isDateCompleteCallback}
                        isDateValidCallback={isDateValidCallback}
                      />
                    </div>
                  );
                }
              } else if (row.fn_type === FnType.SELECT || row.fn_lowername === 'ob_f42') {
                input = (
                  <select className="custom-select" name={row.fn_lowername} value={row.ob_content} onChange={handleChangeInput}>
                    {row.fn_lowername !== 'ob_f47' && <option />}
                    {row.options.map((option:Option) => <option key={option.id} value={option.id}>{option.text}</option>)}
                  </select>
                );
              } else if (row.fn_type === FnType.DATE) {
                const dateValue = getDateValue(row.ob_content);
                input = <input type="date" name={row.fn_lowername} value={dateValue} onChange={handleChangeInput} />;
              } else {
                input = <span>{row.fn_type}</span>;
              }

              return (
                <div>
                  <div className="ve-prop">
                    <div className="ve-label">
                      <label htmlFor={row.fn_lowername}>
                        {row.fn_duty === '1' ? <i>{row.fn_bez}*</i> : row.fn_bez}
                      </label>
                    </div>
                    <div className="ve-input">
                      {input} 
                    </div>
                    <div className="ve-tooltip-icon-div">
                      {row.fn_status_zeilen !== '' && (
                        <button 
                          type="button" 
                          aria-describedby={`${row.fn_lowername}-tooltip`}
                          tabIndex={0}
                          className="ve-tooltip-icon" 
                          onMouseEnter={() => {
                            if (!isCoarsePointer()) handleMouseOverHelp(getVeDataRow(row.fn_lowername)!.fn_lowername);
                          }}
                          onMouseLeave={() => {
                            if (!isCoarsePointer()) handleMouseLeaveHelp(getVeDataRow(row.fn_lowername)!.fn_lowername);
                          }}
                          onPointerDown={(e) => {
                            if (isCoarsePointer()) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleHelp(getVeDataRow(row.fn_lowername)!.fn_lowername);
                            }
                          }}  
                          onFocus={(e) => handleMouseOverHelp(row.fn_lowername)} 
                          onBlur={(e) => handleMouseLeaveHelp(row.fn_lowername)} 
                        > 
                          <img aria-hidden="true" src={tooltip} alt="" />
                        </button>
                      )}
                    </div>  
                  </div>
                  {row.fn_status_zeilen !== '' && getShowHelpValue(row.fn_lowername) && (
                    <div role="tooltip" id={`${row.fn_lowername}-tooltip`} className="ve-help-text">
                      {row.fn_status_zeilen}
                    </div>
                  )}
                </div>
              );
            }
            
            return null;   
          })}

        </form>
        
        <NachObenButton />
        <Footer />
        { popupBoxOn 
          ? 
          (
            <PopupBox 
              boxKind={popupBoxParsRef.current?.kind} 
              boxTitle={popupBoxParsRef.current?.title} 
              boxText={popupBoxParsRef.current?.text}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onCancel={handleCancel} 
            />
          )
          : 
          null }
      </main>
      <NachObenButton />
      <Footer />
    </>
  );
}

function Datumsfeld({ name, value, required, veData, setVeData, format, setDateChanged, getDatierung, isDateCompleteCallback, isDateValidCallback }:DatumsfeldProps) {
  const [year, setYear] = useState<string>(value.slice(0, 4));
  const [month, setMonth] = useState<string>(value.slice(4, 6));
  const [day, setDay] = useState<string>(value.slice(6, 8));

  const dateRef = useRef(value?.slice(0, 8));
  const dayRef = useRef<string>(value.slice(6, 8));
  const monthRef = useRef<string>(value.slice(4, 6));
  const yearRef = useRef<string>(value.slice(0, 4));

  useEffect(() => {  
    setYear(value?.slice(0, 4));
    yearRef.current = value?.slice(0, 4);
    if (format === DatumsFormatOptions.DAY_MONTH_YEAR) {
      setDay(value?.slice(6, 8));
      dayRef.current = value?.slice(6, 8);
      setMonth(value?.slice(4, 6));
      monthRef.current = value?.slice(4, 6);
      dateRef.current = value?.slice(0, 8);
      getDatierung(name, value?.slice(0, 8));
    } else if (format === DatumsFormatOptions.MONTH_YEAR) {
      setDay('');
      dayRef.current = '';
      setMonth(value?.slice(4, 6));
      monthRef.current = value?.slice(4, 6);
      dateRef.current = value?.slice(0, 6);
      getDatierung(name, value?.slice(0, 6));
    } else {
      setDay('');
      dayRef.current = '';
      setMonth('');
      monthRef.current = '';
      dateRef.current = value?.slice(0, 4);
      getDatierung(name, value?.slice(0, 4));
    } 
  }, [value]);

  useEffect(() => { dayRef.current = day; }, [day]);
  useEffect(() => { monthRef.current = month; }, [month]);
  useEffect(() => { yearRef.current = year; }, [year]);

  const getLastDayOfMonth = (y: string, m: string) => new Date(Number(y), Number(m), 0).getDate();

  const formatRef = useRef(format);

  useEffect(() => { 
    formatRef.current = format; 

    if (format === DatumsFormatOptions.YEAR) {
      setDay('');
      setMonth('');
      updateDateRef(yearRef.current);
    }
    if (format === DatumsFormatOptions.MONTH_YEAR) {
      setDay('');
      if (monthRef.current === '' && yearRef.current !== '') {
        let newMonth = '';
        if (name === 'ob_f30') {
          newMonth = '01';
        } else {
          newMonth = '12';
        }
        setMonth(newMonth);
        updateDateRef(yearRef.current + newMonth);
      } else {
        updateDateRef(yearRef.current + monthRef.current);
      }
    }
    if (format === DatumsFormatOptions.DAY_MONTH_YEAR) {
      if (yearRef.current !== '') {
        let newDay = '';
        if (name === 'ob_f30') {
          newDay = '01';
        } else {
          newDay = getLastDayOfMonth(yearRef.current, monthRef.current).toString();
        }
        setDay(newDay);
 
        if (monthRef.current === '') {
          let newMonth = '';
          if (name === 'ob_f30') {
            newMonth = '01';
          } else {
            newMonth = '12';
          }
          setMonth(newMonth);
          updateDateRef(yearRef.current + newMonth + newDay);
        } else {
          updateDateRef(yearRef.current + monthRef.current + newDay);
        }
      }
    }
  }, [format]);

  function isDateComplete() {
    if (formatRef.current === DatumsFormatOptions.MONTH_YEAR) {
      const date = monthRef.current + yearRef.current;
      if (date.length !== 0 && date.length !== 6) return false;
    } else if (formatRef.current === DatumsFormatOptions.DAY_MONTH_YEAR) {
      const date = dayRef.current + monthRef.current + yearRef.current;
      if (date.length !== 0 && date.length !== 8) return false;
    }

    return true;
  }

  function isDateValid() {
    if (dateRef.current !== '') {
      if (!/^\d{4}$/.test(year)) return false;
      if (format === DatumsFormatOptions.YEAR) return true;

      if (!/^\d{2}$/.test(month)) return false;
      if (Number(month) < 1 || Number(month) > 12) return false;
      if (format === DatumsFormatOptions.MONTH_YEAR) return true;

      if (!/^\d{2}$/.test(day)) return false;
      const lastDay = getLastDayOfMonth(year, month);
      if (Number(day) < 1 || Number(day) > lastDay) return false;
    }

    return true;
  }

  useEffect(() => {
    isDateCompleteCallback(name, isDateComplete());
    isDateValidCallback(name, isDateValid());
  }, [year, month, day, format]);
  /*
  useEffect(() => {
    console.log('datierung: ' + dateRef.current);
    getDatierung(name, dateRef.current);
    console.log('datierung: ' + dateRef.current);
  }, [year, month, day]);
*/
  function handleChangeDate(d:string) {
    setVeData(veData.map((row) => {
      if (row.fn_lowername === name) {
        return { ...row, ob_content: d };
      }
      return row;
    }));
    // setDateChanged(true);
  }

  const handleDayChange = (event:React.KeyboardEvent<HTMLInputElement>) => {
    const newDay = event.currentTarget.value;
    setDay(newDay);
    setDateChanged(true);
  };

  const handleMonthChange = (event:React.KeyboardEvent<HTMLInputElement>) => {
    const newMonth = event.currentTarget.value;
    setMonth(newMonth);
    setDateChanged(true);
  };

  const handleYearChange = (event:React.KeyboardEvent<HTMLInputElement>) => {
    const newYear = event.currentTarget.value;
    setYear(newYear);
    setDateChanged(true);
  };

  function updateDateRef(newDate: string) {
    if (isDateComplete()) {
      dateRef.current = newDate;
      getDatierung(name, dateRef.current);
    }
    
    // setDateChanged(true);
  }

  function checkInput(event:React.KeyboardEvent<HTMLInputElement>, type:string) {
    if (!/^[0-9]+$/.test(event.currentTarget.value)) return;
    if (type === 'd') {
      let newDay = event.currentTarget.value;
      if (newDay.length === 1) {
        newDay = '0' + newDay;
      }
      updateDateRef(year + month + newDay);
      setDay(newDay); 
    } else if (type === 'm') {
      let newMonth = event.currentTarget.value;
      if (newMonth.length === 1) {
        newMonth = '0' + newMonth;
      }
      updateDateRef(year + newMonth + day);
      setMonth(newMonth);
    } else {
      let newYear = event.currentTarget.value;
      while (newYear.length > 0 && newYear.length < 4) {
        newYear = '0' + newYear;
      }
      updateDateRef(newYear + month + day);
      setYear(newYear);
    }
  }

  const dayInput = (
    <input 
      type="text" 
      aria-required={required}
      required={required}
      maxLength={2} 
      size={2} 
      value={day} 
      onChange={(e:any) => handleDayChange(e)} 
      onBlur={(e:any) => checkInput(e, 'd')} 
    />
  );
  const monthInput = (
    <input 
      type="text" 
      maxLength={2} 
      size={2} 
      value={month} 
      onChange={(e:any) => handleMonthChange(e)} 
      onBlur={(e:any) => checkInput(e, 'm')} 
    />
  );
  const yearInput = (
    <input 
      type="text" 
      maxLength={4} 
      size={4} 
      value={year} 
      onChange={(e:any) => handleYearChange(e)} 
      onBlur={(e:any) => checkInput(e, 'y')} 
    />
  );
  
  const dateInput = <input type="hidden" name={name} value={dateRef.current} />;
  const dd = <input type="hidden" name={name + '_dd'} value={day} />;
  const mm = <input type="hidden" name={name + '_mm'} value={month} />;
  const yy = <input type="hidden" name={name + '_yy'} value={year} />;

  if (format === 3) {
    return <> {yearInput} {dateInput} {dd} {mm} {yy} </>;
  }

  if (format === 2) {
    return <> {monthInput}.{yearInput} {dateInput} {dd} {mm} {yy} </>;
  }

  return <>{dayInput}.{monthInput}.{yearInput} {dateInput} {dd} {mm} {yy}</>;
}

function DropdownMenu({ option, changeDateFormat, setDateChanged, showPopupBox, openDialog }:DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const menuItems = [
    { optionCode: DatumsFormatOptions.YEAR, optionName: 'Jahresdatum' },
    { optionCode: DatumsFormatOptions.MONTH_YEAR, optionName: 'Monatsdatum' },
    { optionCode: DatumsFormatOptions.DAY_MONTH_YEAR, optionName: 'Tagesdatum' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const check = (
    <img src={checkIcon} width="" height="" alt="" />
  );

  function handleButtonClick() {
    setIsOpen(!isOpen);
    setFocusedIndex(0);
  }

  async function handleOptionClick(opt:number) {
    showPopupBox(BoxType.CONFIRM_OK_ABBRECHEN, 'Datumsformat ändern', alertMessages.CHECK_DATE_ENTRYS);

    const result = await openDialog();
    if (result === 'ja') {
      changeDateFormat(menuItems[opt].optionCode);
    } 
    setDateChanged(true);
    setIsOpen(false);
  }

  function handleButtonKeyDown(event:React.KeyboardEvent<HTMLButtonElement >) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);
    }
  }

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(null);
      buttonRef.current?.focus();
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev === null || prev === menuItems.length - 1 ? 0 : prev + 1));
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev === null || prev === 0 ? menuItems.length - 1 : prev - 1));
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex !== null) {
        handleOptionClick(focusedIndex);
      }
    }

    if (e.key === 'Tab') {
      setIsOpen(false);
      setFocusedIndex(null);
    }
  };

  useEffect(() => {
    if (isOpen && focusedIndex !== null && menuRef.current) {
      const item = menuRef.current.children[focusedIndex].children[0] as HTMLDivElement;
      item?.focus();
    }
  }, [focusedIndex, isOpen]);

  return (
    <>
      <button 
        type="button" 
        ref={buttonRef}
        className={isOpen ? 'dateformat-button dateformat-button-active' : 'dateformat-button'} 
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="dateformat-menu"
        onClick={handleButtonClick} 
        onKeyDown={(e) => handleButtonKeyDown(e)}
        title="Wählen Sie aus dei Möglichkeiten, wie das Datum angezeigt werden soll"
      >
        <span>Datum</span>
        { /* <img src={calendar} width="" height="" alt="" /> */}
      </button>
      {isOpen && (
        <div>
          <ul 
            id="dateformat-menu"
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            onKeyDown={handleMenuKeyDown}
          >
            {menuItems.map((item, index) => (
              <li key={item.optionName}>
                <div
                  role="menuitemradio"
                  tabIndex={-1}
                  onClick={() => handleOptionClick(index)} 
                  aria-checked={option === item.optionCode}
                >
                  {item.optionName} {option === item.optionCode && check}
                </div>
              </li>
            ))}
            
          </ul>
        </div>
      )}
    </>
  );
}

export default VeFormular;
